/* eslint-disable import/no-cycle */
import { makeCrudPaths } from '@/utils'
import { concat } from 'ramda'

const {
  add: addCompany,
  edit: editCompany,
  editWithKey: editCompanyWithKey,
} = makeCrudPaths('company')

const {
  view: viewLocation,
  edit: editLocation,
  add: addLocation,
  addWithKey: addLocationWithKey,
  viewWithKey: viewLocationWithKey,
  editWithKey: editLocationWithKey,
} = makeCrudPaths('location')

const {
  edit: editPayment,
  editWithKey: editPaymentWithKey,
  add: addPayment,
  addWithKey: addPaymentWithKey,
} = makeCrudPaths('payment')

const { add: addUser, addWithKey: addUserWithKey } = makeCrudPaths('user')

const {
  view: viewRecommendation,
  viewWithKey: viewRecommendationWithKey,
} = makeCrudPaths('recommendation')

const viewClaim = '/claims/:companyId/:claimId/:tab?'
const viewClaimWithKey = ({
  companyId,
  claimId,
  tab = 'messages',
}: {
  companyId: string
  claimId: string
  tab?: string
}) => `/claims/${companyId}/${claimId}/${tab}`

const addInsurance = '/location/:locationId/insurance/add'
const addInsuranceWithKey = (locationId: string) =>
  `/location/${locationId}/insurance/add`

const viewInsurance =
  '/location/:locationId/:categoryId/insurance/view/:insuranceId?/:tab?'

const viewInsuranceWithKey = ({
  locationId,
  categoryId,
  insuranceId,
  tab = 'overview',
}: {
  locationId: string
  categoryId: string
  insuranceId?: string
  tab?: string
}) => {
  const basePath = `/location/${locationId}/${categoryId}/insurance/view`
  return insuranceId ? `${basePath}/${insuranceId}/${tab}` : basePath
}

const requestOffer = '/location/request-offer/:locationId/:categoryId'
const requestOfferWithKey = ({
  locationId,
  categoryId,
}: {
  locationId: string
  categoryId: string
}) => `/location/request-offer/${locationId}/${categoryId}`

export const paths = {
  addCompany,
  editCompany,
  editCompanyWithKey,
  poaCompany: `/company/poa/:companyId`,
  authRedirect: '/',
  editLocation,
  editLocationWithKey,
  addLocation: `${addLocation}/:companyId?`,
  addNewLocation: addLocation,
  addLocationWithKey,
  viewLocation,
  viewLocationWithKey,
  addInsurance,
  addInsuranceWithKey,
  viewInsurance,
  viewInsuranceWithKey,
  dashboard: '/dashboard',
  terms: '/terms',
  policy: '/privacy-policy',
  profile: '/profile/:section?/:id?',
  initialInformation: '/initial-information',
  imprint: '/imprint',
  referral: '/referralprogram',
  viewRecommendation,
  viewRecommendationWithKey,
  checkout: `/checkout/:locationId`,
  viewCheckoutWithKey: concat('/checkout/'),
  cart: '/cart/:locationId',
  viewCartWithKey: concat('/cart/'),
  services: `/services`,
  editPayment,
  editPaymentWithKey,
  addPayment: `${addPayment}/:companyId`,
  addPaymentWithKey,
  addUser: `${addUser}/:companyId?`,
  addNewUser: addUser,
  addUserWithKey,
  signup: '/signup',
  assessment: '/location/assessment/:locationId',
  assessmentWithKey: concat('/location/assessment/'),
  whySurein: '/why-surein',
  findInsurances: '/location/find-insurances/:locationId',
  findInsurancesWithKey: concat('/location/find-insurances/'),
  myProfile: '/profile/profile',
  signPoa: '/profile/poa',
  language: '/profile/language',
  payments: '/profile/payments',
  companies: '/profile/companies',
  integrations: '/profile/integrations',
  users: '/profile/users',
  viewOrderConfirmation:
    '/location/:locationId/order-confirmation/:insuranceId',
  viewOrderConfirmationWithKey: ({
    locationId,
    insuranceId,
  }: {
    locationId: string
    insuranceId: string
  }) => `/location/${locationId}/order-confirmation/${insuranceId}`,
  requestOffer,
  requestOfferWithKey,
  // Start TODO: Funnel type should be dynamic and not these hardcoded versions
  landingPageLiabilitySSF: '/assessment/offer-liability',
  liabilitySSF: '/assessment/offer-liability/start',
  liabilitySSFB: '/assessment/offer-liability-b/start',
  landingPageContentSSF: '/assessment/offer-content',
  contentSSF: '/assessment/offer-content/start',
  landingPageLegalSSF: '/assessment/offer-legal',
  legalSSF: '/assessment/offer-legal/start',
  landingPageCyberSSF: '/assessment/offer-cyber',
  cyberSSF: '/assessment/offer-cyber/start',
  landingPageElectronicsSSF: '/assessment/offer-electronics',
  electronicsSSF: '/assessment/offer-electronics/start',
  landingPageLiabilityLead: '/assessment/liability',
  liabilityLead: '/assessment/liability/start',
  landingPageLiabilityLeadB: '/assessment/liability-b',
  liabilityLeadB: '/assessment/liability-b/start',
  landingPageCyberLead: '/assessment/cyber',
  cyberLead: '/assessment/cyber/start',
  landingPageFinancialLossLead: '/assessment/financial-loss',
  financialLossLead: '/assessment/financial-loss/start',
  landingPageProfessionalLiabilityLead: '/assessment/professional-liability',
  professionalLiabilityLead: '/assessment/professional-liability/start',
  landingPageBuildingLead: '/assessment/building',
  buildingLead: '/assessment/building/start',
  landingPageContentLead: '/assessment/content',
  contentLead: '/assessment/content/start',
  landingPageLegalLead: '/assessment/legal',
  legalLead: '/assessment/legal/start',
  // End: Funnel type should be dynamic and not these hardcoded versions
  partnerAssessment: '/:partnerId/:categoryId',
  partnerAssessmentWithKey: ({
    partnerId,
    categoryId,
  }: {
    partnerId: string
    categoryId: string
  }) => `/${partnerId}/${categoryId}`,
  funnel: '/funnel',
  ssfCheckout: '/ssf/checkout/:categoryId',
  viewSsfCheckoutWithKey: (categoryId: string) => `/ssf/checkout/${categoryId}`,
  ssfOffer: '/ssf/offer/:categoryId',
  viewSsfOfferWithKey: (categoryId: string) => `/ssf/offer/${categoryId}`,
  contractsOverview: '/contracts-overview',
  documents: '/documents',
  claims: '/claims',
  viewClaim,
  viewClaimWithKey,
  yearlyAssessment: `/yearlyAssessment/:locationId`,
  viewYearlyCheckWithKey: (locationId: string) =>
    `/yearlyAssessment/${locationId}`,
  integrationsRedirect: '/integrations-redirect',
  integrationsRedirectFailed: '/integrations-redirect-failed',
  addInsuranceContract: '/add-insurance',
}
